import { useEffect, useId, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/shared/lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { INBOX_AGENT_KEY } from './useInboxAgent';

type EventType = 'INSERT' | 'UPDATE' | 'DELETE';

interface RealtimeConfig {
  /** Supabase table to subscribe to */
  table: string;
  /** Filter by specific column value, e.g. 'workspace_id=eq.abc123' */
  filter?: string;
  /** Which events to listen for */
  events?: EventType[];
  /** Query keys to invalidate when an event fires */
  invalidateKeys?: string[][];
  /** Custom callback for fine-grained handling */
  onEvent?: (event: EventType, payload: Record<string, unknown>) => void;
  /** Whether the subscription is active (default true) */
  enabled?: boolean;
}

/**
 * Subscribe to Supabase Realtime postgres_changes for a table.
 * Automatically invalidates TanStack Query keys on changes.
 */
export function useRealtimeSubscription({
  table,
  filter,
  events = ['INSERT', 'UPDATE', 'DELETE'],
  invalidateKeys = [],
  onEvent,
  enabled = true,
}: RealtimeConfig) {
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);
  // The subscription is keyed on table/filter only; read the latest keys and
  // callback through refs so a re-render's new handler is the one that fires.
  const instanceId = useId();
  const invalidateKeysRef = useRef(invalidateKeys);
  const onEventRef = useRef(onEvent);
  invalidateKeysRef.current = invalidateKeys;
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return;

    // realtime-js hands back the existing channel for a topic it already
    // has, so two consumers of the same table+filter shared one, and the
    // first to unmount removed the other's. Each subscription gets its own.
    const channelName = `realtime:${table}:${filter ?? 'all'}:${instanceId}`;
    const channel = supabase.channel(channelName);

    for (const event of events) {
      channel.on(
        'postgres_changes' as 'system',
        {
          event,
          schema: 'public',
          table,
          ...(filter ? { filter } : {}),
        } as Record<string, string>,
        (payload: { eventType: string; new: Record<string, unknown>; old: Record<string, unknown> }) => {
          // Invalidate specified query keys
          for (const key of invalidateKeysRef.current) {
            void queryClient.invalidateQueries({ queryKey: key });
          }

          // Fire custom callback
          onEventRef.current?.(event, payload.new ?? payload.old ?? {});
        },
      );
    }

    channel.subscribe();
    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, filter, enabled, instanceId]);
}

/**
 * Pre-configured subscription for post status changes.
 * Invalidates posts query when a post is created, updated, or deleted.
 */
export function usePostsRealtime(workspaceId: string | undefined) {
  useRealtimeSubscription({
    table: 'posts',
    filter: workspaceId ? `workspace_id=eq.${workspaceId}` : undefined,
    invalidateKeys: [['posts']],
    enabled: !!workspaceId,
  });
}

/**
 * Pre-configured subscription for inbox events.
 * Invalidates the inbox queries when new messages arrive, and the inbox
 * agent's conversation states too, so a colleague's escalate, assign or
 * resolve shows without a reload.
 */
export function useInboxRealtime(workspaceId: string | undefined): void {
  useRealtimeSubscription({
    table: 'inbox_events',
    filter: workspaceId ? `workspace_id=eq.${workspaceId}` : undefined,
    invalidateKeys: [['inbox'], [INBOX_AGENT_KEY, 'states']],
    enabled: !!workspaceId,
  });
}

/**
 * Pre-configured subscription for notifications.
 */
export function useNotificationsRealtime(userId: string | undefined) {
  useRealtimeSubscription({
    table: 'notifications',
    filter: userId ? `user_id=eq.${userId}` : undefined,
    invalidateKeys: [['notifications']],
    enabled: !!userId,
  });
}
